import { useState } from "react";
import { toast } from "sonner";
import {
  useGetAdminMcpStatus,
  useTestAdminMcp,
  useListExternalMcpServers,
  useCreateExternalMcpServer,
  useUpdateExternalMcpServer,
  useDeleteExternalMcpServer,
  useTestExternalMcpServer,
  useListMcpTokens,
  useCreateMcpToken,
  useRevokeMcpToken,
  useListAdminUsers,
  getGetAdminMcpStatusQueryKey,
  getListExternalMcpServersQueryKey,
  getListMcpTokensQueryKey,
  getListAdminUsersQueryKey,
  type ExternalMcpServer,
  type McpToken,
  type AdminUser,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { toastError } from "@/lib/toast-error";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CheckCircle2,
  Copy,
  Key,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  XCircle,
  Wifi,
  WifiOff,
  ToggleLeft,
  ToggleRight,
  Server,
  ShieldOff,
  AlertCircle,
} from "lucide-react";
import { formatDateTime } from "@/lib/format";

type AuthType = "none" | "bearer" | "header";

interface ExternalMcpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing?: ExternalMcpServer | null;
  onSaved: () => void;
}

function ExternalMcpDialog({ open, onOpenChange, editing, onSaved }: ExternalMcpDialogProps) {
  const [name, setName] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [authType, setAuthType] = useState<AuthType>("none");
  const [credential, setCredential] = useState("");
  const [enabled, setEnabled] = useState(true);

  const createMutation = useCreateExternalMcpServer();
  const updateMutation = useUpdateExternalMcpServer();
  const isSaving = createMutation.isPending || updateMutation.isPending;

  function resetAndOpen() {
    if (editing) {
      setName(editing.name);
      setBaseUrl(editing.baseUrl);
      setAuthType((editing.authType as AuthType) ?? "none");
      setCredential("");
      setEnabled(editing.enabled);
    } else {
      setName("");
      setBaseUrl("");
      setAuthType("none");
      setCredential("");
      setEnabled(true);
    }
  }

  function handleOpenChange(val: boolean) {
    if (val) resetAndOpen();
    onOpenChange(val);
  }

  function handleSave() {
    const trimmedName = name.trim();
    const trimmedUrl = baseUrl.trim();
    if (!trimmedName) { toast.error("Name is required"); return; }
    if (!trimmedUrl) { toast.error("Base URL is required"); return; }

    if (editing) {
      updateMutation.mutate(
        {
          id: editing.id,
          data: {
            name: trimmedName,
            baseUrl: trimmedUrl,
            authType,
            credential: credential || undefined,
            enabled,
          },
        },
        {
          onSuccess: () => {
            toast.success("MCP server updated");
            onSaved();
            onOpenChange(false);
          },
          onError: (err) => toastError(err, "Failed to update"),
        },
      );
    } else {
      createMutation.mutate(
        {
          data: {
            name: trimmedName,
            baseUrl: trimmedUrl,
            authType,
            credential: credential || undefined,
            enabled,
          },
        },
        {
          onSuccess: () => {
            toast.success("MCP server registered");
            onSaved();
            onOpenChange(false);
          },
          onError: (err) => toastError(err, "Failed to register"),
        },
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit MCP Server" : "Register External MCP Server"}</DialogTitle>
          <DialogDescription>
            {editing
              ? "Update the connection details for this external MCP server."
              : "Add an external MCP server that TDM can connect to for AI integrations."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="mcp-name">Display Name <span className="text-destructive">*</span></Label>
            <Input
              id="mcp-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My MCP Server"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mcp-url">Base URL <span className="text-destructive">*</span></Label>
            <Input
              id="mcp-url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://mcp.example.com"
              className="font-mono text-sm"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mcp-auth-type">Authentication</Label>
            <Select value={authType} onValueChange={(v) => setAuthType(v as AuthType)}>
              <SelectTrigger id="mcp-auth-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="bearer">Bearer Token (Authorization header)</SelectItem>
                <SelectItem value="header">API Key (X-Api-Key header)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {authType !== "none" && (
            <div className="space-y-1.5">
              <Label htmlFor="mcp-credential">
                {authType === "bearer" ? "Bearer Token" : "API Key"}
                {editing?.hasCredential && (
                  <span className="ml-2 text-xs text-muted-foreground font-normal">
                    (leave blank to keep existing)
                  </span>
                )}
              </Label>
              <Input
                id="mcp-credential"
                type="password"
                value={credential}
                onChange={(e) => setCredential(e.target.value)}
                placeholder={editing?.hasCredential ? "••••••••" : "Enter token or key"}
                className="font-mono"
              />
              {editing?.hasCredential && (
                <p className="text-xs text-muted-foreground">
                  A credential is stored encrypted. Enter a new value to replace it.
                </p>
              )}
            </div>
          )}

          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={() => setEnabled(!enabled)}
              className="flex items-center gap-2 text-sm"
            >
              {enabled ? (
                <ToggleRight className="h-5 w-5 text-primary" />
              ) : (
                <ToggleLeft className="h-5 w-5 text-muted-foreground" />
              )}
              <span className={enabled ? "text-foreground" : "text-muted-foreground"}>
                {enabled ? "Enabled" : "Disabled"}
              </span>
            </button>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editing ? "Update" : "Register"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface ExternalMcpRowProps {
  server: ExternalMcpServer;
  onEdit: (server: ExternalMcpServer) => void;
  onDeleted: () => void;
  onUpdated: () => void;
}

function ExternalMcpRow({ server, onEdit, onDeleted, onUpdated }: ExternalMcpRowProps) {
  const deleteMutation = useDeleteExternalMcpServer();
  const updateMutation = useUpdateExternalMcpServer();
  const testMutation = useTestExternalMcpServer();
  const [testResult, setTestResult] = useState<{ reachable: boolean; error?: string | null } | null>(null);

  function handleDelete() {
    deleteMutation.mutate(
      { id: server.id },
      {
        onSuccess: () => {
          toast.success("MCP server removed");
          onDeleted();
        },
        onError: (err) => toastError(err, "Failed to delete"),
      },
    );
  }

  function handleToggleEnabled() {
    updateMutation.mutate(
      { id: server.id, data: { enabled: !server.enabled } },
      {
        onSuccess: () => {
          toast.success(server.enabled ? "Server disabled" : "Server enabled");
          onUpdated();
        },
        onError: (err) => toastError(err, "Failed to update"),
      },
    );
  }

  function handleTest() {
    setTestResult(null);
    testMutation.mutate(
      { id: server.id },
      {
        onSuccess: (result) => {
          setTestResult(result);
          onUpdated();
          if (result.reachable) {
            toast.success("Connection successful");
          } else {
            toast.error(`Connection failed: ${result.error ?? "Unknown error"}`);
          }
        },
        onError: (err) => toastError(err, "Test failed"),
      },
    );
  }

  const lastStatus = testResult?.reachable !== undefined
    ? testResult.reachable
    : server.lastTestStatus === "ok";
  const hasTestResult = testResult !== null || !!server.lastTestStatus;

  return (
    <div className="p-3 border rounded-lg bg-muted/10 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{server.name}</span>
            {server.enabled ? (
              <Badge variant="outline" className="text-xs border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950/30 dark:text-green-400">
                Enabled
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs text-muted-foreground">
                Disabled
              </Badge>
            )}
            {hasTestResult && (
              lastStatus ? (
                <span className="inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-400">
                  <CheckCircle2 className="h-3 w-3" /> Reachable
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs text-destructive">
                  <XCircle className="h-3 w-3" /> Unreachable
                </span>
              )
            )}
          </div>
          <div className="text-xs text-muted-foreground font-mono mt-0.5 truncate">
            {server.baseUrl}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-xs text-muted-foreground capitalize">
              Auth: {server.authType === "none" ? "None" : server.authType === "bearer" ? "Bearer token" : "API key"}
              {server.hasCredential && " (configured)"}
            </span>
            {server.lastTestedAt && (
              <span className="text-xs text-muted-foreground">
                · Tested {formatDateTime(server.lastTestedAt)}
              </span>
            )}
          </div>
          {(testResult?.error || server.lastTestError) && !lastStatus && (
            <div className="text-xs text-destructive mt-1 font-mono">
              {testResult?.error ?? server.lastTestError}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleTest}
            disabled={testMutation.isPending}
            title="Test connection"
          >
            {testMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Wifi className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleToggleEnabled}
            disabled={updateMutation.isPending}
            title={server.enabled ? "Disable" : "Enable"}
          >
            {updateMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : server.enabled ? (
              <ToggleRight className="h-3.5 w-3.5 text-primary" />
            ) : (
              <ToggleLeft className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onEdit(server)}
            title="Edit"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
                disabled={deleteMutation.isPending}
                title="Delete"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove "{server.name}"?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently remove this external MCP server registration. The remote server itself is not affected.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Remove
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}

interface MintTokenDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  users: AdminUser[];
  onMinted: () => void;
}

function MintTokenDialog({ open, onOpenChange, users, onMinted }: MintTokenDialogProps) {
  const [userId, setUserId] = useState<string>("");
  const [label, setLabel] = useState("");
  const [mintedToken, setMintedToken] = useState<string | null>(null);
  const [mintedFor, setMintedFor] = useState<string>("");
  const createMutation = useCreateMcpToken();

  function handleOpenChange(val: boolean) {
    if (val) {
      setUserId("");
      setLabel("");
      setMintedToken(null);
      setMintedFor("");
    }
    onOpenChange(val);
  }

  function handleMint() {
    if (!userId) {
      toast.error("Pick a user first");
      return;
    }
    createMutation.mutate(
      { data: { userId, label: label.trim() || undefined } },
      {
        onSuccess: (result) => {
          setMintedToken(result.token);
          setMintedFor(result.record.userEmail);
          onMinted();
        },
        onError: (err) => toastError(err, "Failed to mint token"),
      },
    );
  }

  async function handleCopy() {
    if (!mintedToken) return;
    try {
      await navigator.clipboard.writeText(mintedToken);
      toast.success("Token copied to clipboard");
    } catch {
      toast.error("Failed to copy. Select and copy manually.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{mintedToken ? "Token Created" : "Mint MCP API Token"}</DialogTitle>
          <DialogDescription>
            {mintedToken
              ? `Copy this token now — it will not be shown again.`
              : "Generate a new API token that lets an AI client (Claude Desktop, Cursor, etc.) act as the selected user."}
          </DialogDescription>
        </DialogHeader>

        {mintedToken ? (
          <div className="space-y-3 pt-2">
            <div className="rounded border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 p-3">
              <p className="flex items-center gap-2 text-xs font-medium text-amber-800 dark:text-amber-300">
                <AlertCircle className="h-3.5 w-3.5" /> Save this token now
              </p>
              <p className="text-xs text-amber-800 dark:text-amber-300 mt-1">
                Once you close this dialog, the plain-text token cannot be retrieved.
                Only the last 4 characters are stored for display.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Token (for {mintedFor})</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs font-mono bg-muted border rounded px-2 py-2 break-all select-all">
                  {mintedToken}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  onClick={handleCopy}
                  title="Copy token"
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            <div className="flex justify-end pt-2 border-t">
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label htmlFor="token-user">
                User <span className="text-destructive">*</span>
              </Label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger id="token-user">
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  {users.length === 0 ? (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground">
                      No users found
                    </div>
                  ) : (
                    users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        <span className="flex items-center gap-2">
                          {u.email}
                          {u.isAdmin && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0">
                              admin
                            </Badge>
                          )}
                        </span>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The token will run MCP tool calls as this user (their permissions, their data).
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="token-label">Label (optional)</Label>
              <Input
                id="token-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Claude Desktop"
                maxLength={80}
              />
            </div>

            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleMint} disabled={createMutation.isPending || !userId}>
                {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Mint Token
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface McpTokenRowProps {
  token: McpToken;
  onRevoked: () => void;
}

function McpTokenRow({ token, onRevoked }: McpTokenRowProps) {
  const revokeMutation = useRevokeMcpToken();

  function handleRevoke() {
    revokeMutation.mutate(
      { id: token.id },
      {
        onSuccess: () => {
          toast.success("Token revoked");
          onRevoked();
        },
        onError: (err) => toastError(err, "Failed to revoke token"),
      },
    );
  }

  return (
    <div className="p-3 border rounded-lg bg-muted/10 space-y-1">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm truncate">{token.userEmail}</span>
            {token.label && (
              <Badge variant="outline" className="text-xs">
                {token.label}
              </Badge>
            )}
            <code className="text-xs font-mono text-muted-foreground">
              ••••{token.lastFour}
            </code>
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap text-xs text-muted-foreground">
            <span>Created {formatDateTime(token.createdAt)}</span>
            {token.createdByEmail && <span>· by {token.createdByEmail}</span>}
            {token.lastUsedAt ? (
              <span>· Last used {formatDateTime(token.lastUsedAt)}</span>
            ) : (
              <span>· Never used</span>
            )}
          </div>
        </div>

        <div className="shrink-0">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
                disabled={revokeMutation.isPending}
                title="Revoke token"
              >
                {revokeMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Revoke this token?</AlertDialogTitle>
                <AlertDialogDescription>
                  Any AI client using <code className="font-mono">••••{token.lastFour}</code>
                  {token.label ? ` (${token.label})` : ""} on behalf of{" "}
                  <strong>{token.userEmail}</strong> will immediately stop working. This cannot
                  be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleRevoke}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Revoke
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}

function McpTokensSection() {
  const queryClient = useQueryClient();
  const [showMintDialog, setShowMintDialog] = useState(false);

  const { data: tokens, isLoading: isLoadingTokens } = useListMcpTokens({
    query: { queryKey: getListMcpTokensQueryKey() },
  });
  const { data: users } = useListAdminUsers({
    query: { queryKey: getListAdminUsersQueryKey() },
  });

  function invalidateTokens() {
    queryClient.invalidateQueries({ queryKey: getListMcpTokensQueryKey() });
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Key className="h-4 w-4" /> MCP API Tokens
        </h3>
        <Button variant="outline" size="sm" onClick={() => setShowMintDialog(true)}>
          <Plus className="mr-2 h-3.5 w-3.5" />
          Mint Token
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Tokens let AI clients authenticate against the built-in MCP server as a specific user.
        Stored encrypted; the plain-text value is shown only once at creation. Newly minted or
        revoked tokens take effect within ~30 seconds without restarting the MCP server.
      </p>

      {isLoadingTokens ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : tokens && tokens.length > 0 ? (
        <div className="space-y-2">
          {tokens.map((token) => (
            <McpTokenRow key={token.id} token={token} onRevoked={invalidateTokens} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-8 border border-dashed rounded-lg text-center">
          <ShieldOff className="h-8 w-8 text-muted-foreground/40 mb-2" />
          <p className="text-sm text-muted-foreground">No MCP API tokens yet.</p>
          <p className="text-xs text-muted-foreground mt-1">
            Mint one to let an AI client connect to the built-in MCP server.
          </p>
        </div>
      )}

      <MintTokenDialog
        open={showMintDialog}
        onOpenChange={setShowMintDialog}
        users={users ?? []}
        onMinted={invalidateTokens}
      />
    </div>
  );
}

export function McpServersSection() {
  const queryClient = useQueryClient();
  const { data: mcpStatus, isLoading: isLoadingStatus } = useGetAdminMcpStatus({
    query: { queryKey: getGetAdminMcpStatusQueryKey() },
  });
  const { data: externalServers, isLoading: isLoadingExternal } = useListExternalMcpServers({
    query: { queryKey: getListExternalMcpServersQueryKey() },
  });

  const testBuiltInMutation = useTestAdminMcp();
  const [builtInTestResult, setBuiltInTestResult] = useState<{
    reachable: boolean;
    error?: string | null;
  } | null>(null);
  const [isTestingBuiltIn, setIsTestingBuiltIn] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingServer, setEditingServer] = useState<ExternalMcpServer | null>(null);

  async function handleCopyUrl() {
    if (!mcpStatus?.baseUrl) return;
    try {
      await navigator.clipboard.writeText(mcpStatus.baseUrl);
      toast.success("MCP server URL copied");
    } catch {
      toast.error("Failed to copy. Select and copy manually.");
    }
  }

  function handleTestBuiltIn() {
    setIsTestingBuiltIn(true);
    testBuiltInMutation.mutate(undefined, {
      onSuccess: (result) => {
        setBuiltInTestResult(result);
        setIsTestingBuiltIn(false);
        queryClient.invalidateQueries({ queryKey: getGetAdminMcpStatusQueryKey() });
        if (result.reachable) {
          toast.success("MCP server is reachable");
        } else {
          toast.error(`MCP server unreachable: ${result.error ?? "Unknown error"}`);
        }
      },
      onError: (err) => {
        setIsTestingBuiltIn(false);
        toastError(err, "Test failed");
      },
    });
  }

  function invalidateExternal() {
    queryClient.invalidateQueries({ queryKey: getListExternalMcpServersQueryKey() });
  }

  function openAdd() {
    setEditingServer(null);
    setShowDialog(true);
  }

  function openEdit(server: ExternalMcpServer) {
    setEditingServer(server);
    setShowDialog(true);
  }

  const currentReachable = builtInTestResult !== null
    ? builtInTestResult.reachable
    : mcpStatus?.reachable;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Server className="h-4 w-4" /> Built-in MCP Server
        </h3>
        <p className="text-xs text-muted-foreground">
          The TDM MCP server allows AI assistants (Claude Desktop, Cursor, etc.) to
          interact with your deal data via the Model Context Protocol.
        </p>
        {isLoadingStatus ? (
          <Skeleton className="h-24 w-full" />
        ) : mcpStatus ? (
          <div className="p-4 border rounded-lg bg-muted/10 space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground font-medium">Base URL</span>
                  {currentReachable === true && (
                    <span className="inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-400">
                      <CheckCircle2 className="h-3 w-3" /> Reachable
                    </span>
                  )}
                  {currentReachable === false && (
                    <span className="inline-flex items-center gap-1 text-xs text-destructive">
                      <WifiOff className="h-3 w-3" /> Unreachable
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-xs font-mono bg-background border rounded px-2 py-1.5 break-all">
                    {mcpStatus.baseUrl}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={handleCopyUrl}
                    title="Copy URL"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground">
                  Transport: <span className="font-medium uppercase">{mcpStatus.transport}</span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleTestBuiltIn}
                disabled={isTestingBuiltIn}
                className="shrink-0"
              >
                {isTestingBuiltIn ? (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Wifi className="mr-2 h-3.5 w-3.5" />
                )}
                Test Connection
              </Button>
            </div>

            {builtInTestResult?.error && !builtInTestResult.reachable && (
              <div className="text-xs text-destructive font-mono">
                {builtInTestResult.error}
              </div>
            )}

            <div className="p-3 rounded border bg-background space-y-1.5">
              <p className="text-xs font-medium">How to connect an AI client</p>
              <p className="text-xs text-muted-foreground">
                In Claude Desktop or Cursor, add an MCP server with type{" "}
                <code className="font-mono bg-muted px-1 rounded">streamable-http</code> and set
                the URL to the base URL above. Use an MCP API token for the{" "}
                <code className="font-mono bg-muted px-1 rounded">Authorization: Bearer</code>{" "}
                header. Contact your administrator to obtain a token.
              </p>
            </div>
          </div>
        ) : null}
      </div>

      <McpTokensSection />

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">External MCP Servers</h3>
          <Button variant="outline" size="sm" onClick={openAdd}>
            <Plus className="mr-2 h-3.5 w-3.5" />
            Add Server
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Register external MCP servers that TDM can connect to for future AI integrations.
          Credentials are stored encrypted.
        </p>

        {isLoadingExternal ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : externalServers && externalServers.length > 0 ? (
          <div className="space-y-2">
            {externalServers.map((server) => (
              <ExternalMcpRow
                key={server.id}
                server={server}
                onEdit={openEdit}
                onDeleted={invalidateExternal}
                onUpdated={invalidateExternal}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 border border-dashed rounded-lg text-center">
            <Server className="h-8 w-8 text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">No external MCP servers registered yet.</p>
            <p className="text-xs text-muted-foreground mt-1">
              Add external MCP servers to enable outbound AI integrations.
            </p>
          </div>
        )}
      </div>

      <ExternalMcpDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        editing={editingServer}
        onSaved={invalidateExternal}
      />
    </div>
  );
}
